// src/core/color.ts
import type { Hsl, Rgb, Lab } from '../types';

// HSL → "#RRGGBB" hex string (uppercase). Convenience for displaying the
// player's submitted color as a Pantone-card receipt.
export function hslToHex(hsl: Hsl): string {
  const { r, g, b } = hslToRgb(hsl);
  const h = (n: number) => n.toString(16).padStart(2, '0').toUpperCase();
  return `#${h(r)}${h(g)}${h(b)}`;
}

// HSL → RGB per CSS Color spec (h: 0–360, s/l: 0–100; rgb: 0–255 ints)
export function hslToRgb({ h, s, l }: Hsl): Rgb {
  const sN = s / 100;
  const lN = l / 100;
  const c = (1 - Math.abs(2 * lN - 1)) * sN;
  const hp = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let r1 = 0, g1 = 0, b1 = 0;
  if (hp >= 0 && hp < 1) { r1 = c; g1 = x; b1 = 0; }
  else if (hp < 2) { r1 = x; g1 = c; b1 = 0; }
  else if (hp < 3) { r1 = 0; g1 = c; b1 = x; }
  else if (hp < 4) { r1 = 0; g1 = x; b1 = c; }
  else if (hp < 5) { r1 = x; g1 = 0; b1 = c; }
  else { r1 = c; g1 = 0; b1 = x; }
  const m = lN - c / 2;
  return {
    r: Math.round((r1 + m) * 255),
    g: Math.round((g1 + m) * 255),
    b: Math.round((b1 + m) * 255),
  };
}

// sRGB gamma decode → linear RGB in [0, 1]
export function rgbToLinear({ r, g, b }: Rgb): { r: number; g: number; b: number } {
  const dec = (c: number) => {
    const cn = c / 255;
    return cn <= 0.04045 ? cn / 12.92 : Math.pow((cn + 0.055) / 1.055, 2.4);
  };
  return { r: dec(r), g: dec(g), b: dec(b) };
}

// linear sRGB → CIE XYZ (D65)
export function linearToXyz({ r, g, b }: { r: number; g: number; b: number }): { x: number; y: number; z: number } {
  // sRGB D65 matrix
  const x = r * 0.4124564 + g * 0.3575761 + b * 0.1804375;
  const y = r * 0.2126729 + g * 0.7151522 + b * 0.0721750;
  const z = r * 0.0193339 + g * 0.1191920 + b * 0.9503041;
  return { x: x * 100, y: y * 100, z: z * 100 };
}

// XYZ (D65) → CIE Lab
export function xyzToLab({ x, y, z }: { x: number; y: number; z: number }): Lab {
  // D65 reference white
  const Xn = 95.047, Yn = 100.000, Zn = 108.883;
  const f = (t: number) => t > 0.008856 ? Math.cbrt(t) : (7.787 * t) + (16 / 116);
  const fx = f(x / Xn), fy = f(y / Yn), fz = f(z / Zn);
  return {
    L: (116 * fy) - 16,
    a: 500 * (fx - fy),
    b: 200 * (fy - fz),
  };
}

export function hslToLab(hsl: Hsl): Lab {
  return xyzToLab(linearToXyz(rgbToLinear(hslToRgb(hsl))));
}

// Shortest hue distance on a circle (degrees). Returns 0–180.
export function circularDelta(a: number, b: number): number {
  const d = Math.abs(((a - b) % 360 + 540) % 360 - 180);
  return d;
}
